<template>
  <div class="store-selector-wrapper">
    <div v-if="loading" class="store-loading">
      <i class="ri-loader-4-line spin"></i> 載入門市中...
    </div>

    <div v-else class="store-selector">
      <!-- 篩選輸入框 -->
      <div class="store-search">
        <i class="ri-search-line"></i>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="searchPlaceholder"
          class="store-search-input"
          @focus="$event.target.select()"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="store-search-clear"
          @click="searchQuery = ''"
        >
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- 門市按鈕列表 -->
      <div class="store-btn-list">
        <button
          v-if="clearable"
          type="button"
          class="store-btn"
          :class="{ active: !modelValue }"
          @click="select('')"
        >
          <i class="ri-close-circle-line"></i>
          {{ clearLabel }}
        </button>
        <button
          v-for="store in filteredStores"
          :key="store.id"
          type="button"
          class="store-btn"
          :class="{ active: modelValue === store.id }"
          @click="select(store.id)"
        >
          <i class="ri-store-2-line"></i>
          <span class="store-btn-name">{{ store.storeName }}</span>
          <span v-if="showCode" class="store-btn-code">{{ store.storeCode }}</span>
        </button>
        <div v-if="filteredStores.length === 0 && searchQuery" class="store-empty">
          找不到符合「{{ searchQuery }}」的門市
        </div>
        <div v-else-if="stores.length === 0" class="store-empty">
          尚無可用門市
        </div>
      </div>
    </div>

    <!-- 已選標示（slot 供外部自訂，或使用預設） -->
    <div v-if="modelValue && selectedStore" class="selected-store-hint">
      <slot name="hint" :store="selectedStore">
        <i class="ri-check-line"></i>
        已選擇
        <span class="selected-store-name">{{ selectedStore.storeName }}</span>
        <span v-if="showCode" class="selected-code">{{ selectedStore.storeCode }}</span>
      </slot>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'StoreSelector',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    stores: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    clearLabel: {
      type: String,
      default: '不指定'
    },
    showCode: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      default: '搜尋門市名稱或代碼...'
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const searchQuery = ref('');

    const filteredStores = computed(() => {
      if (!searchQuery.value.trim()) return props.stores;
      const q = searchQuery.value.trim().toLowerCase();
      return props.stores.filter(s =>
        s.storeName.toLowerCase().includes(q) ||
        (s.storeCode && s.storeCode.toLowerCase().includes(q))
      );
    });

    const selectedStore = computed(() =>
      props.stores.find(s => s.id === props.modelValue) || null
    );

    const select = (id) => {
      emit('update:modelValue', id);
      emit('change', id);
    };

    // 外部清除 modelValue 時也清掉搜尋
    watch(() => props.modelValue, (val) => {
      if (!val) searchQuery.value = '';
    });

    return {
      searchQuery,
      filteredStores,
      selectedStore,
      select
    };
  }
};
</script>

<style scoped>
.store-loading {
  color: #6b7280;
  font-size: 14px;
  padding: 8px 0;
}

.store-selector {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.store-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.store-search > i {
  color: #9ca3af;
  font-size: 16px;
  flex-shrink: 0;
}

.store-search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #111827;
}

.store-search-input::placeholder {
  color: #9ca3af;
}

.store-search-clear {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.store-search-clear:hover {
  color: #374151;
}

.store-btn-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  max-height: 220px;
  overflow-y: auto;
}

.store-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 20px;
  background: white;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.store-btn:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #2563eb;
}

.store-btn.active {
  border-color: #2563eb;
  background: #2563eb;
  color: white;
}

.store-btn-name {
  font-weight: 500;
}

.store-btn-code {
  font-size: 11px;
  opacity: 0.7;
}

.store-btn.active .store-btn-code {
  opacity: 0.85;
}

.store-empty {
  width: 100%;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 12px 0;
}

.selected-store-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 14px;
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 8px;
  font-size: 14px;
  color: #1d4ed8;
}

.selected-store-hint i {
  font-size: 16px;
  color: #2563eb;
  flex-shrink: 0;
}

.selected-store-name {
  font-weight: 600;
  color: #1e40af;
}

.selected-code {
  font-size: 12px;
  color: #60a5fa;
  background: #dbeafe;
  padding: 1px 7px;
  border-radius: 10px;
}

.spin {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
